import React, { useMemo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { getConfigProp, SiteConfigManager } from '@atlas/core';
import {
  CLIENT_PROFILE_COMPONENT_TYPES,
  clientProfileRoutes,
  COMPONENT_TYPES,
} from '@atlas/routing';

import BonusesFeedRoute from 'components/ClientProfile/Bonuses/BonusesFeedRoute';
import BonusesHistory from 'components/ClientProfile/Bonuses/BonusesHistory/BonusesHistory';
import {
  BONUSES_FREEBETS_SEGMENT,
  BONUSES_FREESPINS_SEGMENT,
  BONUSES_HISTORY_SEGMENT,
  LEGACY_BONUSES_DEPOSIT_SEGMENT,
} from 'components/ClientProfile/Bonuses/bonusesTabRoutes';
import FreeBets from 'components/ClientProfile/Bonuses/FreeBets/FreeBets';

const { CLIENT_PROFILE } = COMPONENT_TYPES;
const { bonuses } = clientProfileRoutes;
const { BONUSES, BONUSES_DEPOSIT, BONUSES_FREEBET } = CLIENT_PROFILE_COMPONENT_TYPES;

const BonusesRouter = () => {
  const isSportRadarProject = getConfigProp('isSportRadarProject');
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const bonusesPath = `${clientProfilePath}/${bonuses}`;

  const { isExpandedOnMobile = false, tabs = [] } = useMemo(
    () => SiteConfigManager.getSubComponentOptions(CLIENT_PROFILE, BONUSES),
    [],
  );

  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const hideFreebets = hideSportBonuses && !isExpandedOnMobile;

  // CMS gating for the two visible bonus pages - combined feed (parent path) and
  // freebets. History stays deep-link-only regardless of CMS; the old Freespins
  // route was removed (FT-8675) and now redirects into the combined feed
  const isCombinedActive = useMemo(
    () => tabs.some(t => t.type === BONUSES_DEPOSIT && t.isActive),
    [tabs],
  );
  const isFreebetsActive = useMemo(
    () =>
      !isSportRadarProject &&
      !hideFreebets &&
      tabs.some(t => t.type === BONUSES_FREEBET && t.isActive),
    [tabs, isSportRadarProject, hideFreebets],
  );

  // Picks the parent-path destination when only one visible tab is active -
  // matches the dev-branch ProfileLayoutRouter behaviour.
  const defaultPath = useMemo(() => {
    if (isCombinedActive) return bonusesPath;
    if (isFreebetsActive) return `${bonusesPath}/${BONUSES_FREEBETS_SEGMENT}`;
    return clientProfilePath;
  }, [bonusesPath, clientProfilePath, isCombinedActive, isFreebetsActive]);

  return (
    <Switch>
      {/*
        Legacy redirects
      */}
      <Redirect exact from={`${bonusesPath}/${LEGACY_BONUSES_DEPOSIT_SEGMENT}`} to={bonusesPath} />
      <Redirect
        from={`${bonusesPath}/${LEGACY_BONUSES_DEPOSIT_SEGMENT}/:bonusId`}
        to={`${bonusesPath}/:bonusId`}
      />
      <Redirect exact from={`${bonusesPath}/${BONUSES_FREESPINS_SEGMENT}`} to={bonusesPath} />
      <Redirect
        from={`${bonusesPath}/${BONUSES_FREESPINS_SEGMENT}/:freespinId`}
        to={`${bonusesPath}/:freespinId`}
      />

      {/*
        CMS-gated parent-path redirect. When the combined feed is disabled but
        freebets is active, /profile/bonuses must land on /profile/bonuses/freebets
      */}
      {defaultPath !== bonusesPath && <Redirect exact from={bonusesPath} to={defaultPath} />}

      {hideFreebets && (
        <Redirect exact from={`${bonusesPath}/${BONUSES_FREEBETS_SEGMENT}`} to={bonusesPath} />
      )}

      {/*
        New routes. Order matters: literal-segment routes are listed before the catch-all `:bonusId` so `/bonuses/freebets` is not interpreted as a bonus id.
        Freebets and the combined feed are gated by CMS; history remains deep-link reachable regardless
      */}
      {isFreebetsActive && (
        <Route component={FreeBets} path={`${bonusesPath}/${BONUSES_FREEBETS_SEGMENT}/:bonusId?`} />
      )}
      <Route component={BonusesHistory} path={`${bonusesPath}/${BONUSES_HISTORY_SEGMENT}`} />
      {isCombinedActive && <Route component={BonusesFeedRoute} path={`${bonusesPath}/:bonusId?`} />}

      <Redirect to={defaultPath} />
    </Switch>
  );
};

export default BonusesRouter;
